import { PartImageNarrowTarget } from '@/api/api';
import { PartImageCandidate } from '@/api/generatedApi';
import { ImageMatchConfidencePill } from '@/components/PartFinder/confidence/ConfidencePill';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NarrowByMeasurements } from './NarrowByMeasurements';

const Panes = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: '16px',
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridTemplateRows: 'minmax(0, 1fr)',
    flex: 1,
    minHeight: 0,
  },
}));

const PartRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
}));

export interface MeasuringItem {
  target: PartImageNarrowTarget;
  label: string;
  description: string;
}

export interface MeasureItemViewProperties {
  identificationId: string;
  measuring: MeasuringItem;
  candidates: PartImageCandidate[];
  pick: string;
  onPick: (niin: string) => void;
}

const PartChoice = ({
  candidate,
  picked,
  onPick,
}: {
  candidate: PartImageCandidate;
  picked: boolean;
  onPick: () => void;
}) => (
  <PartRow data-testid='part-image-measure-part'>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant='body2' noWrap>
        {candidate.nsn} {candidate.itemName ?? ''}
      </Typography>
      {candidate.measured && (
        <Typography variant='caption' color='text.secondary'>
          Matches your measurements
        </Typography>
      )}
    </Box>
    <ImageMatchConfidencePill score={candidate.score} />
    {picked && <Chip size='small' color='primary' label='Picked' />}
    {!picked && (
      <Button size='small' variant='outlined' onClick={onPick}>
        Use
      </Button>
    )}
  </PartRow>
);

export const MeasureItemView = ({
  identificationId,
  measuring,
  candidates,
  pick,
  onPick,
}: MeasureItemViewProperties) => {
  const pickTop = (narrowed: PartImageCandidate[]) => {
    if (narrowed[0]) onPick(narrowed[0].niin);
  };
  return (
    <Stack gap='12px' sx={{ flex: { md: 1 }, minHeight: 0 }} data-testid='part-image-measure-view'>
      {measuring.description && (
        <Typography variant='body2' color='text.secondary'>
          {measuring.description}
        </Typography>
      )}
      <Panes>
        <NarrowByMeasurements identificationId={identificationId} target={measuring.target} onNarrowed={pickTop} />
        <Stack gap='8px' sx={{ overflowY: { md: 'auto' }, minHeight: 0, pr: '4px' }}>
          <Typography variant='subtitle2'>Parts for {measuring.label}</Typography>
          {candidates.length === 0 && (
            <Typography variant='body2' color='text.secondary' data-testid='part-image-measure-no-parts'>
              No FLIS parts yet. Measure it, and the parts that match are listed here.
            </Typography>
          )}
          {candidates.map(candidate => (
            <PartChoice
              key={candidate.niin}
              candidate={candidate}
              picked={candidate.niin === pick}
              onPick={() => onPick(candidate.niin)}
            />
          ))}
        </Stack>
      </Panes>
    </Stack>
  );
};
